import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { formatPrice } from "../lib/format";
import { useNoIndex } from "../lib/usePageMeta";
import { Truck, Spinner, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { trackBeginCheckout, trackAddShippingInfo, trackAddPaymentInfo, trackCheckoutError } from "../lib/analytics";
import { readAttribution } from "../lib/attribution";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const emptyAddr = () => ({
  name: "", street1: "", street2: "", city: "", state: "", zip: "",
  country: "US", phone: "", email: "",
});

// Mirrors backend `_normalise_phone` in routes/checkout_shipping.py.
const PHONE_STRIP_RE = /[^\d+]/g;
function normalisePhone(v) {
  if (!v) return "";
  const raw = String(v).trim().replace(PHONE_STRIP_RE, "");
  if (!raw) return "";
  if (raw.startsWith("+")) {
    const digits = raw.slice(1);
    if (!/^\d+$/.test(digits) || digits.length < 7 || digits.length > 15) return "";
    return "+" + digits;
  }
  if (raw.length === 10 && /^\d+$/.test(raw)) return "+1" + raw;
  if (raw.length === 11 && raw.startsWith("1") && /^\d+$/.test(raw)) return "+" + raw;
  if (/^\d+$/.test(raw) && raw.length >= 7 && raw.length <= 15) return "+" + raw;
  return "";
}

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
const ZIP_RE = /^\d{5}(-?\d{4})?$/;

export default function CheckoutShipping() {
  useNoIndex("/checkout/shipping", {
    title: "Checkout · Shipping",
    description: "Confirm shipping address and live carrier rates before secure Stripe checkout for Medical Peptide Store reference standards.",
  });
  const nav = useNavigate();
  const { items, refresh } = useCart();

  const [addr, setAddr] = useState(() => {
    try {
      const cached = sessionStorage.getItem("mps_checkout_addr_v1");
      return cached ? { ...emptyAddr(), ...JSON.parse(cached) } : emptyAddr();
    } catch { return emptyAddr(); }
  });
  const [touched, setTouched] = useState({});
  const [cartReady, setCartReady] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [rates, setRates] = useState(null);
  const [shipmentId, setShipmentId] = useState("");
  const [selectedRateId, setSelectedRateId] = useState("");
  const [expiresAt, setExpiresAt] = useState(0);
  const [continuing, setContinuing] = useState(false);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    let cancelled = false;
    refresh().finally(() => { if (!cancelled) setCartReady(true); });
    return () => { cancelled = true; };
  }, [refresh]);
  useEffect(() => {
    if (cartReady && items.length === 0) nav("/cart");
  }, [cartReady, items.length, nav]);

  const setField = (k, v) => setAddr((a) => ({ ...a, [k]: v }));
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));

  // ── Per-field errors ────────────────────────────────────────────────
  const errors = useMemo(() => {
    const e = {};
    if (!(addr.name || "").trim())     e.name    = "Recipient name is required.";
    if (!(addr.street1 || "").trim())  e.street1 = "Street address is required.";
    if (!(addr.city || "").trim())     e.city    = "City is required.";
    if (!(addr.state || "").trim())    e.state   = "State is required.";
    if (!ZIP_RE.test((addr.zip || "").trim())) e.zip = "Enter a valid 5-digit ZIP.";
    if (!(addr.email || "").trim())    e.email   = "Email is required for the order confirmation.";
    else if (!EMAIL_RE.test((addr.email || "").trim())) e.email = "That email doesn't look valid.";
    if (!(addr.phone || "").trim())    e.phone   = "Phone is required — carrier needs a contact number.";
    else if (!normalisePhone(addr.phone)) e.phone = "Enter a US 10-digit or +CC international phone.";
    return e;
  }, [addr]);

  const addrIsValid = Object.keys(errors).length === 0;

  const fetchRates = async () => {
    if (!addrIsValid) {
      // Touch every field so error text renders below any that were skipped.
      setTouched({ name:1, street1:1, city:1, state:1, zip:1, email:1, phone:1 });
      toast.error("Please fix the highlighted contact/shipping fields.");
      return;
    }
    setFetching(true); setRates(null); setFetchError("");
    try {
      const outbound = { ...addr, phone: normalisePhone(addr.phone), email: (addr.email || "").trim().toLowerCase() };
      try { sessionStorage.setItem("mps_checkout_addr_v1", JSON.stringify(outbound)); } catch {}
      const { data } = await api.post("/checkout/shipping/rates", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        to_address: outbound,
      });
      setRates(data.rates || []);
      setShipmentId(data.shipment_id);
      setExpiresAt(data.expires_at);
      setSelectedRateId(data.rates?.[0]?.rate_id || "");
    } catch (err) {
      // Never surface the raw carrier response — extract the first user-facing detail
      // if present, otherwise show a safe generic message.
      const detail = err?.response?.data?.detail;
      let msg = "We couldn't fetch shipping rates right now. Please double-check your address or contact support.";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length && detail[0]?.msg) msg = detail[0].msg;
      setFetchError(msg);
      setRates([]);
    } finally {
      setFetching(false);
    }
  };

  const cont = async () => {
    if (!selectedRateId) {
      toast.error("Please choose a shipping option.");
      try { trackCheckoutError({ error_stage: "shipping_selection", error_code: "validation_error" }); } catch (_) {}
      return;
    }
    if (!addrIsValid) {
      toast.error("Please complete the required contact + address fields.");
      try { trackCheckoutError({ error_stage: "shipping_selection", error_code: "validation_error" }); } catch (_) {}
      return;
    }
    setContinuing(true);
    try {
      const outbound = { ...addr, phone: normalisePhone(addr.phone), email: (addr.email || "").trim().toLowerCase() };
      const sel = await api.post("/checkout/shipping/select", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        to_address: outbound,
        shipment_id: shipmentId,
        rate_id: selectedRateId,
      });
      const attribution = readAttribution();
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping_method_id: selectedRateId,
        shipping_quote_token: sel.data.quote_token,
        contact_email: outbound.email,
        contact_phone: outbound.phone,
        attribution,
      });
      try {
        trackBeginCheckout({
          items,
          subtotal_cents: items.reduce((s, i) => s + (i.line_total_cents || 0), 0),
          currency: "USD",
          transaction_id: data.session_id,
        });
      } catch (_) {}
      // Fire add_payment_info immediately BEFORE the redirect to Stripe.
      // This is the last event we can emit ourselves — after the redirect
      // the hosted Stripe page owns the URL and we lose the JS runtime.
      try {
        trackAddPaymentInfo({
          items,
          subtotal_cents: items.reduce((s, i) => s + (i.line_total_cents || 0), 0),
          currency: "USD",
          session_id: data.session_id,
        });
      } catch (_) {}
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Checkout is currently unavailable. Please try again.";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length && detail[0]?.msg) msg = detail[0].msg;
      toast.error(msg);
      // Emit a sanitised drop-off event — never the raw server text.
      const quoteFail = typeof msg === "string" && msg.toLowerCase().includes("quote");
      try {
        trackCheckoutError({
          error_stage: "checkout_session_create",
          error_code:  quoteFail ? "quote_expired" : "session_create_failed",
        });
      } catch (_) {}
      if (quoteFail) {
        setRates(null); setSelectedRateId(""); setShipmentId("");
      }
    } finally {
      setContinuing(false);
    }
  };

  return (
    <div className="bg-brand-surface min-h-screen" data-testid="checkout-shipping-page" data-clarity-mask="true">
      <div className="mps-container py-14 md:py-20">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow mb-4">Checkout · step 1 of 2</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
            Shipping details &amp; live rate
          </h1>
          <p className="mt-4 text-brand-muted">
            Every order ships signature-required. Rates are quoted live from our carriers based on your destination.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-8">
            <div className="border border-brand-border bg-white p-6" data-testid="shipping-address-form">
              <div className="overline mb-3 flex items-center gap-2"><Truck size={12} weight="duotone" className="text-brand-accent" /> Destination address</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Recipient name" required value={addr.name}    onChange={(v) => setField("name", v)}    onBlur={() => markTouched("name")}    error={touched.name && errors.name}       testid="ship-name"    fullWidth />
                <Field label="Street address" required value={addr.street1} onChange={(v) => setField("street1", v)} onBlur={() => markTouched("street1")} error={touched.street1 && errors.street1} testid="ship-street1" fullWidth />
                <Field label="Apt / Suite"             value={addr.street2} onChange={(v) => setField("street2", v)} testid="ship-street2" fullWidth />
                <Field label="City" required           value={addr.city}    onChange={(v) => setField("city", v)}    onBlur={() => markTouched("city")}    error={touched.city && errors.city}       testid="ship-city" />
                <div className="col-span-1">
                  <label className="text-[10px] text-brand-muted uppercase tracking-widest font-mono">
                    State <span className="text-red-600" aria-hidden="true">*</span>
                  </label>
                  <select
                    value={addr.state}
                    onChange={(e) => setField("state", e.target.value)}
                    onBlur={() => markTouched("state")}
                    data-testid="ship-state"
                    aria-required="true"
                    aria-invalid={!!(touched.state && errors.state)}
                    className={`mt-1 w-full bg-brand-surface border px-3 py-2 font-mono text-brand-navy ${touched.state && errors.state ? "border-red-400" : "border-brand-border"}`}
                  >
                    <option value="">Select</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {touched.state && errors.state && <ErrorMsg testid="ship-state-error">{errors.state}</ErrorMsg>}
                </div>
                <Field label="ZIP" required value={addr.zip}   onChange={(v) => setField("zip", v)}   onBlur={() => markTouched("zip")}   error={touched.zip && errors.zip}   testid="ship-zip" inputMode="numeric" />
                <Field label="Phone" required type="tel" value={addr.phone} onChange={(v) => setField("phone", v)} onBlur={() => markTouched("phone")} error={touched.phone && errors.phone} testid="ship-phone" placeholder="e.g. (702) 555-0100" helper="US 10-digit or +CC international" />
                <Field label="Email" required type="email" value={addr.email} onChange={(v) => setField("email", v)} onBlur={() => markTouched("email")} error={touched.email && errors.email} testid="ship-email" fullWidth placeholder="you@lab.example" />
              </div>
              <div className="mt-5 flex items-center gap-3">
                <button
                  onClick={fetchRates}
                  disabled={fetching || !addrIsValid}
                  data-testid="fetch-rates-btn"
                  className="bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-6 py-3 text-sm tracking-wide disabled:opacity-40"
                >
                  {fetching ? "Getting rates…" : "Get shipping rates"}
                </button>
                <p className="text-[11px] text-brand-muted font-mono">US destinations only.</p>
              </div>
            </div>

            <div className="border border-brand-border bg-white p-6" data-testid="shipping-rates-list">
              <div className="overline mb-3 flex items-center gap-2"><Truck size={12} weight="duotone" className="text-brand-accent" /> Available rates</div>
              {rates === null && (
                <p className="text-brand-muted text-sm">Enter your address to see live shipping rates from our carriers.</p>
              )}
              {rates !== null && rates.length === 0 && (
                <div className="flex items-start gap-3 border border-red-200 bg-red-50 text-red-800 p-4" data-testid="shipping-rates-error">
                  <WarningCircle size={18} className="mt-0.5" />
                  <div className="text-sm">
                    {fetchError || "No shipping services are available for this destination."}
                  </div>
                </div>
              )}
              {rates !== null && rates.length > 0 && (
                <div className="space-y-2" data-testid="shipping-rate-options">
                  {rates.map((r) => (
                    <label
                      key={r.rate_id}
                      className={`flex items-center justify-between border p-4 cursor-pointer transition-colors ${selectedRateId === r.rate_id ? "border-brand-navy bg-brand-surface" : "border-brand-border hover:bg-brand-surface"}`}
                      data-testid={`shipping-rate-option-${r.rate_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="rate"
                          checked={selectedRateId === r.rate_id}
                          onChange={() => {
                            setSelectedRateId(r.rate_id);
                            try {
                              trackAddShippingInfo({
                                items,
                                subtotal_cents: items.reduce((s, i) => s + (i.line_total_cents || 0), 0),
                                currency: "USD",
                                rate_id: r.rate_id,
                                shipping_tier: `${r.carrier} ${r.service}`,
                              });
                            } catch (_) {}
                          }}
                          data-testid={`shipping-rate-radio-${r.rate_id}`}
                        />
                        <div>
                          <div className="font-semibold text-brand-navy">{r.carrier} · {r.service}</div>
                          <div className="text-[11px] text-brand-muted font-mono">
                            {r.estimated_days != null ? `Est. ${r.estimated_days} business day${r.estimated_days === 1 ? "" : "s"}` : ""}
                            {r.duration_terms ? ` — ${r.duration_terms}` : ""}
                          </div>
                        </div>
                      </div>
                      <div className="text-right font-mono text-brand-navy" data-testid={`shipping-rate-price-${r.rate_id}`}>
                        {formatPrice(r.amount_cents)}
                      </div>
                    </label>
                  ))}
                  {expiresAt > 0 && (
                    <p className="text-[10px] text-brand-muted font-mono mt-2">
                      These rates hold until {new Date(expiresAt * 1000).toLocaleTimeString()}. If they expire we'll ask you to re-quote before payment.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-1">
            <div className="border border-brand-border bg-white p-6 sticky top-8" data-testid="checkout-summary">
              <div className="overline mb-3 flex items-center gap-2"><CheckCircle size={12} weight="duotone" className="text-brand-accent" /> Order</div>
              <div className="text-sm text-brand-muted mb-4">
                {items.length} {items.length === 1 ? "item" : "items"} in your cart
              </div>
              <button
                onClick={cont}
                disabled={continuing || !selectedRateId || !addrIsValid}
                data-testid="continue-to-payment-btn"
                className="w-full bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-6 py-3 text-sm tracking-wide disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {continuing ? (<><Spinner size={16} className="animate-spin" /> Redirecting to Stripe…</>) : "Continue to payment"}
              </button>
              <p className="mt-3 text-[10px] text-brand-muted font-mono leading-relaxed">
                Your card is charged only once you complete Stripe checkout on the next page. Shipping is charged as quoted above.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, value, onChange, onBlur, testid, fullWidth, type = "text", inputMode, placeholder, helper, error }) {
  return (
    <div className={fullWidth ? "col-span-2" : "col-span-1"}>
      <label className="text-[10px] text-brand-muted uppercase tracking-widest font-mono">
        {label}
        {required && <span className="text-red-600" aria-hidden="true"> *</span>}
      </label>
      <input
        type={type}
        inputMode={inputMode}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        data-testid={testid}
        aria-required={required || undefined}
        aria-invalid={!!error}
        required={required || undefined}
        placeholder={placeholder}
        className={`mt-1 w-full bg-brand-surface border px-3 py-2 font-mono text-brand-navy ${error ? "border-red-400" : "border-brand-border"}`}
      />
      {helper && !error && <p className="mt-1 text-[10px] text-brand-muted font-mono">{helper}</p>}
      {error && <ErrorMsg testid={`${testid}-error`}>{error}</ErrorMsg>}
    </div>
  );
}

function ErrorMsg({ testid, children }) {
  return <p className="mt-1 text-[11px] text-red-600" data-testid={testid}>{children}</p>;
}
